import { Plane } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

export default function Terms() {
  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3">
              <img 
                src="/logo-small.png" 
                alt="PetFlyCheck Logo" 
                className="w-12 h-12 object-contain"
              />
              <h1 className="text-2xl font-bold text-slate-900">PetFlyCheck</h1>
            </a>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-16">
        <h2 className="text-4xl font-bold text-slate-900 mb-8">Terms & Conditions</h2>

        <Card>
          <CardContent className="p-8 prose prose-slate max-w-none">
            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">1. Service Description</h3>
              <p className="text-slate-700 leading-relaxed">
                PetFlyCheck provides an AI-assisted pre-check only. It is not an official airline service. 
                Our analysis uses artificial intelligence to review pet travel crate photos and documents 
                against IATA guidelines and airline-specific rules (when available).
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">2. No Guarantee of Acceptance</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                <strong>IMPORTANT:</strong> Final decision remains with the airline and airport/ground staff. 
                We do not guarantee acceptance at any airport or by any airline.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Rules may change at any time without notice. Users must verify airline-specific requirements 
                directly with their carrier before travel.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">3. Limitation of Liability</h3>
              <p className="text-slate-700 leading-relaxed">
                PetFlyCheck is not liable for:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-2">
                <li>Travel disruption, missed flights, or additional fees</li>
                <li>Airport refusals or rejections</li>
                <li>Changes in airline policies after our analysis</li>
                <li>Incomplete or unclear photos/documents provided by users</li>
                <li>Differences between AI analysis and actual airline interpretation</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">4. User Responsibilities</h3>
              <p className="text-slate-700 leading-relaxed">
                Users are responsible for:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-2">
                <li>Providing clear, accurate photos and documents</li>
                <li>Verifying current airline requirements before travel</li>
                <li>Contacting airlines directly for final confirmation</li>
                <li>Ensuring all documentation is up to date</li>
                <li>Following all airline-specific pet travel procedures</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">5. Refund Policy</h3>
              <p className="text-slate-700 leading-relaxed">
                Full refund if your report missed an IATA criterion that was verifiable from the photos and
                measurements you provided. Email contact@petflycheck.io with your submission ID and the criterion
                you believe was missed.
              </p>
              <p className="text-slate-700 leading-relaxed mt-3">
                PetFlyCheck is an independent service, not affiliated with IATA or any airline. Our report is not an
                airline approval; the airline makes the final decision.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">6. Data Usage & Privacy</h3>
              <p className="text-slate-700 leading-relaxed">
                We store uploaded photos and documents only to provide the service and for a limited retention 
                period (30 days by default). We do not sell or share user data with third parties. 
                See our <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a> for details.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">7. AI Analysis Disclaimer</h3>
              <p className="text-slate-700 leading-relaxed">
                Our AI analysis is based on visual inspection and pattern recognition. It may not detect 
                all compliance issues. Physical inspection by airline staff remains the authoritative check.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">8. Changes to Terms</h3>
              <p className="text-slate-700 leading-relaxed">
                We reserve the right to modify these terms at any time. Continued use of the service 
                constitutes acceptance of updated terms.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">9. Contact</h3>
              <p className="text-slate-700 leading-relaxed">
                For questions about these terms, contact us at <strong>contact@petflycheck.io</strong>
              </p>
            </section>

            <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-900">
                <strong>Last Updated:</strong> January 2026
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-slate-400">
            <p>© 2026 PetFlyCheck. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
